/**
 * The live assessment roster, shared between Questions and Add questions.
 * Session memory + sessionStorage so Mix, coverage, and section totals read
 * the same paper after Leo or manual add/remove.
 */

import type { LibraryAiSpotted } from "@/lib/library-ai-spotted"
import type {
  AssessmentBankQuestion,
  BloomLevel,
} from "@/lib/mock/assessment-add-questions"
import { isSettledPaperQuestion } from "@/lib/paper-question-metrics"
import { paperBloomFromAuthoring } from "@/lib/question-authoring-seed"

const BLOOM_FROM_LABEL: Record<string, BloomLevel> = {
  Remember: "Remember",
  Remembering: "Remember",
  Recall: "Remember",
  Understand: "Understand",
  Understanding: "Understand",
  Apply: "Apply",
  Applying: "Apply",
  Analyze: "Analyze",
  Analyzing: "Analyze",
  Evaluate: "Evaluate",
  Evaluating: "Evaluate",
  Create: "Create",
  Creating: "Create",
}

export type LivePaperQuestion = {
  id: string
  number: number
  stem: string
  title?: string
  type: string
  topic: string
  difficulty: "Easy" | "Medium" | "Hard" | ""
  difficultyPercentage: number
  pointBiserial: number
  discriminationIndex: number
  blooms: string
  points: number
  usedBefore: boolean
  usedCount: number
  usedInSameCourse: boolean
  sectionId?: string
  pointsLocked?: boolean
  status?: "Draft" | "Ready"
  isNew?: boolean
  author?: string
  authorEmail?: string
  leoThinking?: boolean
  /** Same AI Spotted tag as the question bank catalog. */
  aiSpotted?: LibraryAiSpotted
  fixedSummary?: string
}

/** v2 drops v1 entries, some of which were written empty by an interrupted render. */
export const LIVE_PAPER_RESET_EVENT = "exxat-ds:live-paper-reset"
const LIVE_PAPER_KEY_MARK = "assessment-live-paper:"
const storageKey = (assessmentId: string) =>
  `exxat-ds:assessment-live-paper:v7:${assessmentId}`

const memoryStash = new Map<string, LivePaperQuestion[]>()

function removeMatchingKeys(store: Storage): void {
  const keys: string[] = []
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index)
    if (key?.includes(LIVE_PAPER_KEY_MARK)) keys.push(key)
  }
  for (const key of keys) store.removeItem(key)
}

/** Drop Leo and manual paper edits so the seeded roster can load again. */
export function clearLivePapers(): void {
  memoryStash.clear()
  if (typeof window === "undefined") return
  try {
    removeMatchingKeys(window.sessionStorage)
    removeMatchingKeys(window.localStorage)
    window.dispatchEvent(new Event(LIVE_PAPER_RESET_EVENT))
  } catch {
    /* quota / private mode */
  }
}

function isLivePaperQuestion(value: unknown): value is LivePaperQuestion {
  if (!value || typeof value !== "object") return false
  const row = value as LivePaperQuestion
  return typeof row.id === "string" && typeof row.stem === "string"
}

export function rememberLivePaper(
  assessmentId: string,
  questions: readonly LivePaperQuestion[],
  options?: { allowEmpty?: boolean },
): void {
  if (!assessmentId) return
  // An empty first write is a paper that has not been read yet, not a paper
  // someone emptied. Storing it hides the seeded roster behind a blank tab.
  // Create-assessment passes `allowEmpty` so a new draft stays empty.
  if (
    questions.length === 0 &&
    readLivePaper(assessmentId) == null &&
    !options?.allowEmpty
  ) {
    return
  }
  const payload = questions.map((row) => ({ ...row }))
  memoryStash.set(assessmentId, payload)
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(storageKey(assessmentId), JSON.stringify(payload))
  } catch {
    /* quota / private mode — memory stash still works */
  }
}

/** New drafts start with no items. Demo papers keep using the seeded roster. */
export function rememberEmptyCreatedPaper(assessmentId: string): void {
  rememberLivePaper(assessmentId, [], { allowEmpty: true })
}

export function isEmptyCreatedPaper(assessmentId: string | undefined): boolean {
  if (!assessmentId) return false
  const live = readLivePaper(assessmentId)
  return live != null && live.length === 0
}

export function readLivePaper(assessmentId: string): LivePaperQuestion[] | null {
  if (!assessmentId) return null
  const fromMemory = memoryStash.get(assessmentId)
  if (fromMemory) return fromMemory.map((row) => ({ ...row }))
  if (typeof window === "undefined") return null
  try {
    const raw = window.sessionStorage.getItem(storageKey(assessmentId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return null
    const rows = parsed.filter(isLivePaperQuestion)
    memoryStash.set(assessmentId, rows)
    return rows.map((row) => ({ ...row }))
  } catch {
    return null
  }
}

/**
 * A paper that is scheduled, live, or being marked cannot have no questions —
 * students answered it. An empty store on one of those is a stale write, so the
 * seeded roster wins and the tab keeps agreeing with the grading KPIs.
 */
export function livePaperForStatus(
  paper: LivePaperQuestion[] | null,
  status: string,
): LivePaperQuestion[] | null {
  if (paper == null || paper.length > 0) return paper
  return status === "Draft" ? paper : null
}

export function livePaperToBankQuestion(
  row: LivePaperQuestion,
): AssessmentBankQuestion {
  const difficulty =
    row.difficulty === "Easy" || row.difficulty === "Hard"
      ? row.difficulty
      : "Medium"
  return {
    id: row.id,
    title: row.title ?? row.stem,
    stem: row.stem,
    type: row.type,
    topic: row.topic || "Untagged",
    difficulty,
    sourceKind: "past",
    sourceLabel: "Paper",
    folderId: "",
    pbi: row.usedBefore ? String(row.pointBiserial) : "--",
    points: row.points,
    bloom: row.blooms ? BLOOM_FROM_LABEL[row.blooms] : undefined,
    author: row.author,
    authorEmail: row.authorEmail,
    timesUsed: row.usedCount,
    usedInSameCourse: row.usedInSameCourse,
    neverUsed: !row.usedBefore,
    status: row.status === "Draft" ? "Draft" : "Ready",
    isNew: row.isNew,
  }
}

export function bankQuestionToLivePaper(
  row: AssessmentBankQuestion,
  opts: { number: number; sectionId: string },
): LivePaperQuestion {
  const difficulty =
    row.difficulty === "Easy" || row.difficulty === "Hard"
      ? row.difficulty
      : row.difficulty === "Medium"
        ? "Medium"
        : ""
  return {
    id: row.id,
    number: opts.number,
    stem: row.stem,
    title: row.title,
    type: row.type,
    topic: row.topic,
    difficulty,
    difficultyPercentage: 0,
    pointBiserial: 0,
    discriminationIndex: 0,
    blooms: paperBloomFromAuthoring(row.bloom ?? ""),
    points: row.points ?? 1,
    usedBefore: row.neverUsed === false,
    usedCount: row.timesUsed ?? 0,
    usedInSameCourse: row.usedInSameCourse ?? false,
    sectionId: opts.sectionId,
    status: row.status === "Draft" ? "Draft" : "Ready",
    isNew: row.isNew,
    author: row.author,
    authorEmail: row.authorEmail,
  }
}

export function livePaperForSection(
  paper: readonly LivePaperQuestion[] | null,
  sectionId: string,
): LivePaperQuestion[] {
  if (!paper) return []
  return paper.filter(
    (row) => row.sectionId === sectionId && isSettledPaperQuestion(row),
  )
}
